/** Default checklist templates — mirrors API defaults for store settings editor. */
export type DefaultChecklistItemDef = {
  id: string;
  label: string;
  automated?: boolean;
  link?: string;
};

export const DEFAULT_OPENING_CHECKLIST: DefaultChecklistItemDef[] = [
  { id: 'cash_session', label: 'Open cash session with float amount', automated: true, link: '/cash' },
  { id: 'check_messages', label: 'Check overnight messages and missed calls', link: '/messages' },
  { id: 'review_appointments', label: "Review today's scheduled appointments", link: '/appointments' },
  { id: 'check_parts', label: 'Check for parts arrivals (open POs)', link: '/inventory/purchase-orders' },
  { id: 'review_overdue', label: 'Review overdue tickets from yesterday', link: '/tickets?filter=overdue' },
  { id: 'check_ready', label: 'Confirm ready-for-pickup tickets have been notified', link: '/tickets?status=ready' },
  { id: 'low_stock', label: 'Review low stock items', link: '/inventory?filter=low_stock' },
  { id: 'team_briefing', label: 'Brief team on daily priorities' },
];

export const DEFAULT_CLOSING_CHECKLIST: DefaultChecklistItemDef[] = [
  { id: 'notify_ready', label: 'Notify all ready-for-pickup customers', automated: true, link: '/tickets?status=ready' },
  { id: 'close_cash', label: 'Close cash session and print Z-report', automated: true, link: '/cash' },
  { id: 'review_tomorrow', label: "Review tomorrow's appointments", link: '/appointments' },
  { id: 'submit_orders', label: 'Submit any pending parts orders', link: '/inventory/purchase-orders' },
  { id: 'overdue_calls', label: 'Call customers with overdue tickets', link: '/tickets?filter=overdue' },
  { id: 'nps_followups', label: 'Handle NPS detractor follow-ups', link: '/reports/nps?filter=follow_up' },
  { id: 'secure_store', label: 'Lock up devices, cash drawer, and storefront' },
  { id: 'set_alarm', label: 'Set alarm and confirm security system' },
];

/**
 * Which checklist is "current" for the top bar / banner.
 * Non-overlapping: morning open (before 2pm local), closing from 2pm.
 * When only one type is enabled, always use that type.
 */
export function resolveActiveChecklistType(opts?: {
  openingEnabled?: boolean;
  closingEnabled?: boolean;
}): 'open' | 'close' | null {
  const opening = Boolean(opts?.openingEnabled);
  const closing = Boolean(opts?.closingEnabled);
  if (!opening && !closing) return null;
  if (opening && !closing) return 'open';
  if (!opening && closing) return 'close';
  // Both on: switch at 14:00 local so they never both prompt at once
  return new Date().getHours() < 14 ? 'open' : 'close';
}

/** @deprecated Prefer resolveActiveChecklistType with enable flags */
export function activeChecklistType(): 'open' | 'close' {
  return new Date().getHours() < 14 ? 'open' : 'close';
}

export function checklistProgressColor(completed: number, total: number): string {
  if (total === 0 || completed >= total) return 'bg-green-100 text-green-800 ring-green-200';
  if (completed === 0) return 'bg-red-100 text-red-800 ring-red-200';
  return 'bg-amber-100 text-amber-900 ring-amber-200';
}

export function bannerDismissKey(type: 'open' | 'close', date: string) {
  return `fixflow_checklist_banner_${type}_${date}`;
}

export function isBannerDismissed(type: 'open' | 'close', date: string): boolean {
  try {
    return localStorage.getItem(bannerDismissKey(type, date)) === '1';
  } catch {
    return false;
  }
}

export function dismissBanner(type: 'open' | 'close', date: string) {
  try {
    localStorage.setItem(bannerDismissKey(type, date), '1');
  } catch {
    /* ignore */
  }
}
